import {
  CARVINGS,
  STAMPS,
  SUPERSTITIONS,
  type AppraisalTarget,
  type CarvingId,
  type FinishId,
  type HouseRuleId,
  type HouseRuleSpec,
  type Inscription,
  type OmenKind,
  type StampId,
  type SuperstitionId,
} from '../../engine';

/**
 * What the Counter sells, and for how much (docs/SHOP_DESIGN.md §6–§13). The engine resolves
 * every item; this table only says what is rare, what costs what and when it appears.
 */

export type Rarity = 'common' | 'uncommon' | 'rare' | 'legendary';

export interface ItemInfo {
  readonly rarity: Rarity;
  readonly price: number;
}

const C = (price: number): ItemInfo => ({ rarity: 'common', price });
const U = (price: number): ItemInfo => ({ rarity: 'uncommon', price });
const R = (price: number): ItemInfo => ({ rarity: 'rare', price });
const LEG: ItemInfo = { rarity: 'legendary', price: 20 };

export const SUPERSTITION_INFO: Readonly<Record<SuperstitionId, ItemInfo>> = {
  'red-thread': C(5),
  'blue-bead': C(5),
  'four-leaf': C(5),
  'canary-feather': C(5),
  'odd-fellow': C(4),
  'even-keel': C(4),
  snap: C(4),
  bloodline: R(9),
  'ladder-walker': C(4),
  'mirror-twin': C(4),
  ribbon: C(4),
  'plumb-line': C(4),
  'compass-rose': U(6),
  stairwell: U(6),
  prism: U(6),
  palindrome: U(6),
  'double-knot': R(9),
  'chain-letter': R(10),
  horseshoe: C(4),
  'black-cat': C(5),
  'bottle-tree': U(7),
  'crows-feather': U(6),
  'butchers-hook': R(9),
  'skeleton-key': R(8),
  'paper-crown': C(4),
  'penny-on-the-track': C(5),
  homecoming: U(6),
  'black-mirror': R(8),
  'tallow-candle': R(8),
  rubbings: C(4),
  'mourning-bell': C(5),
  'gravediggers-spade': U(6),
  'widows-veil': U(7),
  'keepsake-locket': U(6),
  'seance-table': R(9),
  'blood-money': C(4),
  'rag-and-bone': C(5),
  'debt-collector': U(6),
  'pawn-ticket': U(7),
  'sacrificial-lamb': R(8),
  'coin-in-the-shoe': C(5),
  'misers-purse': C(4),
  'magpies-hoard': U(7),
  'early-bird': U(6),
  'piggy-bank': U(5),
  'golden-tooth': R(8),
  'first-light': U(6),
  'last-orders': U(6),
  'spilled-salt': C(5),
  'seventh-son': U(6),
  'grandfather-clock': U(7),
  'thirteenth-step': C(4),
  'long-shadow': C(4),
  'salt-line': C(4),
  cornerstone: C(5),
  crossroads: U(6),
  phalanx: U(6),
  'tallow-stub': C(4),
  'long-way-home': U(6),
  'lucky-coin': C(4),
  'loaded-die': U(6),
  wishbone: C(4),
  'rabbits-foot': R(8),
  'mimic-jar': R(10),
  reliquary: R(10),
  'empty-chair': U(6),
  'the-unredeemed': LEG,
  'the-last-pawn': LEG,
  'saints-finger': LEG,
  'pawn-kings-crown': LEG,
  'brokers-monocle': LEG,
};

export const LEGENDARIES = SUPERSTITIONS.filter(
  (id) => SUPERSTITION_INFO[id].rarity === 'legendary',
);

/** Superstitions that only matter between tables (the campaign applies them). */
export const ECONOMY_SUPERSTITIONS: readonly SuperstitionId[] = [
  'coin-in-the-shoe',
  'misers-purse',
  'piggy-bank',
  'golden-tooth',
  'keepsake-locket',
];

export const BENCH_CURIOS = [
  'whittling-knife',
  'ink-pot',
  'file',
  'rubber-stamp',
  'tracing-paper',
  'coin-purse',
  'appraisers-note',
] as const;
export type BenchCurioId = (typeof BENCH_CURIOS)[number];

export const CURIO_PRICE: Readonly<Record<string, number>> = {
  chalk: 3,
  hourglass: 5,
  pliers: 3,
  scissors: 3,
  'bell-jar': 3,
  'smelling-salts': 4,
  'pocket-watch': 3,
  'black-candle': 4,
  lodestone: 3,
  'rag-doll': 3,
  planchette: 5,
  'cracked-mirror': 4,
  spyglass: 3,
  matchbook: 3,
  'whittling-knife': 3,
  'ink-pot': 3,
  file: 3,
  'rubber-stamp': 4,
  'tracing-paper': 4,
  'coin-purse': 3,
  'appraisers-note': 3,
};

/** Appraisals that raise a pattern or feat; the hidden patterns appear from Night II. */
export const APPRAISALS: readonly AppraisalTarget[] = [
  'ladder',
  'echo',
  'streak',
  'fullCircle',
  'none',
  'capture',
  'crown',
  'landing',
  'epitaph',
  'stair',
  'prism',
  'mirror',
];
export const APPRAISAL_PRICE = 3;

export const CARVING_INFO: Readonly<Record<CarvingId, ItemInfo>> = Object.fromEntries(
  CARVINGS.map((id, i) => [id, i < 7 ? C(5) : i < 15 ? U(7) : R(9)]),
) as Record<CarvingId, ItemInfo>;

export const STAMP_PRICE = 1;
export const FINISH_PRICE: Readonly<Record<FinishId, number>> = {
  foil: 2,
  gilt: 3,
  pearl: 5,
  ghost: 5,
};
export const CARD_PRICE = 2;
/** A Trick costs this much more than a plain card (docs/SHOP_DESIGN.md §10.7). */
export const TRICK_PRICE = 2;
export const STAMP_LIST: readonly StampId[] = STAMPS;

// ---------------------------------------------------------------------------------------------
// Lots
// ---------------------------------------------------------------------------------------------

export const LOT_KINDS = [
  'card',
  'hearsay',
  'appraisal',
  'curio',
  'carver',
  'mourning',
  'omen',
  'estate',
] as const;
export type LotKind = (typeof LOT_KINDS)[number];
export type LotSize = 'normal' | 'large' | 'grand';

export const LOT_WEIGHT: Readonly<Record<LotKind, number>> = {
  card: 30,
  appraisal: 25,
  curio: 20,
  hearsay: 15,
  carver: 5,
  mourning: 5,
  omen: 5,
  estate: 3,
};

export const LOT_PRICE: Readonly<Record<LotSize, number>> = { normal: 4, large: 6, grand: 8 };

/** How many a Lot shows and how many may be taken. */
export function lotShape(kind: LotKind, size: LotSize): { show: number; take: number } {
  if (kind === 'hearsay')
    return size === 'normal'
      ? { show: 2, take: 1 }
      : size === 'large'
        ? { show: 4, take: 1 }
        : { show: 4, take: 2 };
  if (kind === 'estate') return size === 'normal' ? { show: 2, take: 1 } : { show: 3, take: 1 };
  if (kind === 'carver' || kind === 'mourning' || kind === 'omen') return { show: 3, take: 1 };
  return size === 'normal'
    ? { show: 3, take: 1 }
    : size === 'large'
      ? { show: 5, take: 1 }
      : { show: 5, take: 2 };
}

/** Omens a player may place with an Omen Lot. */
export const PLACEABLE_OMENS: readonly OmenKind[] = [
  'candle',
  'gilt-tile',
  'salt-circle',
  'well',
  'ink-pool',
  'chalk-x',
  'trapdoor',
  'pillar',
  'lost-ring',
];

/** Ready-made Epitaph routes sold in Mourning Lots. */
export const MOURNING_ROUTES: readonly {
  readonly id: string;
  readonly inscription: Inscription;
}[] = [
  {
    id: 'widows-walk',
    inscription: {
      links: [
        { face: { color: 'R', number: 2 }, wasWild: false, move: { type: 'slide', dx: 0, dy: 2 } },
        { face: { color: 'R', number: 1 }, wasWild: false, move: { type: 'slide', dx: 1, dy: 0 } },
      ],
      recordedScore: 6,
      round: 1,
    },
  },
  {
    id: 'long-goodbye',
    inscription: {
      links: [
        { face: { color: 'B', number: 1 }, wasWild: false, move: { type: 'slide', dx: 1, dy: 1 } },
        { face: { color: 'B', number: 2 }, wasWild: false, move: { type: 'slide', dx: -2, dy: 2 } },
        { face: { color: 'B', number: 1 }, wasWild: false, move: { type: 'slide', dx: 1, dy: 1 } },
      ],
      recordedScore: 12,
      round: 1,
    },
  },
  {
    id: 'knights-lament',
    inscription: {
      links: [
        { face: { color: 'G', number: 2 }, wasWild: false, move: { type: 'knight', dx: 1, dy: 2 } },
        {
          face: { color: 'G', number: 2 },
          wasWild: false,
          move: { type: 'knight', dx: -1, dy: 2 },
        },
      ],
      recordedScore: 8,
      round: 1,
    },
  },
  {
    id: 'mourners-ladder',
    inscription: {
      links: [
        { face: { color: 'R', number: 1 }, wasWild: false, move: { type: 'slide', dx: 0, dy: 1 } },
        { face: { color: 'R', number: 2 }, wasWild: false, move: { type: 'slide', dx: 2, dy: 0 } },
        { face: { color: 'R', number: 3 }, wasWild: false, move: { type: 'slide', dx: 0, dy: 3 } },
      ],
      recordedScore: 15,
      round: 1,
    },
  },
  {
    id: 'last-dance',
    inscription: {
      links: [
        {
          face: { color: 'Y', number: 1 },
          wasWild: false,
          move: { type: 'hops', dirs: [{ dx: 1, dy: 1 }] },
        },
        { face: { color: 'B', number: 1 }, wasWild: false, move: { type: 'slide', dx: -1, dy: 1 } },
      ],
      recordedScore: 6,
      round: 1,
    },
  },
  {
    id: 'pilgrims-circle',
    inscription: {
      links: [
        { face: { color: 'R', number: 1 }, wasWild: false, move: { type: 'slide', dx: 1, dy: 0 } },
        { face: { color: 'B', number: 1 }, wasWild: false, move: { type: 'slide', dx: -1, dy: 1 } },
        { face: { color: 'R', number: 1 }, wasWild: false, move: { type: 'slide', dx: 0, dy: 1 } },
      ],
      recordedScore: 12,
      round: 1,
    },
  },
];

// ---------------------------------------------------------------------------------------------
// Fixtures, Bargains, Favors
// ---------------------------------------------------------------------------------------------

/** Fixtures in pairs: the second appears once the first is bought (§10.10). */
export const FIXTURE_PAIRS: readonly (readonly [string, string])[] = [
  ['glass-case', 'display-wall'],
  ['brass-scale', 'gold-scale'],
  ['strongbox', 'vault-door'],
  ['shop-bell', 'silver-bell'],
  ['coat-hook', 'coat-lining'],
  ['wall-shelf', 'altar-shelf'],
  ['long-fingers', 'sleight-of-hand'],
  ['deep-pockets', 'bottomless-pockets'],
  ['grave-plot', 'family-crypt'],
  ['carvers-bench', 'masters-bench'],
  ['stamp-rack', 'engraving-press'],
  ['appraisers-desk', 'assay-office'],
  ['ledger', 'black-ledger'],
  ['oil-lamp', 'second-sight'],
  ['hiring-board', 'guild-hall'],
];
export const FIXTURES = FIXTURE_PAIRS.flat();
export type FixtureId = string;
export const FIXTURE_PRICE = 10;

export const BARGAINS = [
  'soul-ticket',
  'effigy',
  'graft',
  'fusion',
  'mortgage',
  'blood-ink',
  'burnt-ledger',
  'changeling',
  'hollow-crown',
  'black-wild',
  'exhumation',
  'brokers-hand',
] as const;
export type BargainId = (typeof BARGAINS)[number];

/** What a Bargain costs in pawns at the Back Room (it costs nothing more from an Estate Lot). */
export const BARGAIN_PAWNS: Readonly<Record<BargainId, number>> = {
  'soul-ticket': 2,
  effigy: 1,
  graft: 1,
  fusion: 1,
  mortgage: 0,
  'blood-ink': 1,
  'burnt-ledger': 0,
  changeling: 0,
  'hollow-crown': 1,
  'black-wild': 0,
  exhumation: 1,
  'brokers-hand': 0,
};

export const FAVORS = [
  'sealed-lot',
  'coupon',
  'investment',
  'rare-tip',
  'pearl-tip',
  'double',
  'handful',
  'starlight',
  'speed',
  'bounty',
  'ward',
  'toll-waiver',
  'mourning',
  'carvers',
] as const;
export type FavorId = (typeof FAVORS)[number];

// ---------------------------------------------------------------------------------------------
// Opponents
// ---------------------------------------------------------------------------------------------

export const PATRONS = [
  'sailor',
  'bride',
  'clockmaker',
  'gambler',
  'priest',
  'thief',
  'twins',
  'soldier',
] as const;
export type PatronId = (typeof PATRONS)[number];

/** What a patron brings from Term II: Superstitions for its side, House Rules, extra Omens. */
export const PATRON_QUIRKS: Readonly<
  Record<
    PatronId,
    {
      readonly mantel?: readonly SuperstitionId[];
      readonly rules?: readonly HouseRuleSpec[];
      readonly omens?: readonly OmenKind[];
    }
  >
> = {
  sailor: { omens: ['draft', 'draft'] },
  bride: { mantel: ['widows-veil'] },
  clockmaker: { rules: [{ id: 'clock', n: 6 }] },
  gambler: { mantel: ['loaded-die'] },
  priest: { rules: [{ id: 'warded-start', side: 'N' }] },
  thief: { rules: [{ id: 'pickpocket', n: 1 }] },
  twins: { mantel: ['mirror-twin', 'mirror-twin'] },
  soldier: { mantel: ['phalanx'] },
};

/** The regulars (src/ai/personality.ts order): signature Superstition and House Rules. */
export const REGULAR_KITS: readonly {
  readonly signature: SuperstitionId;
  readonly rules: readonly HouseRuleId[];
}[] = [
  { signature: 'tallow-stub', rules: ['short-wick', 'drip', 'snuffed'] },
  { signature: 'crows-feather', rules: ['sticky-fingers', 'gleam', 'pickpocket'] },
  { signature: 'gravediggers-spade', rules: ['mourning-dress', 'last-rites', 'veiled'] },
];

/** The Broker's second House Rule at Closing Time (Unredeemed always applies). */
export const BROKER_RULES = ['collateral-call', 'final-appraisal', 'foreclosure'] as const;
export type BrokerRule = (typeof BROKER_RULES)[number];

/** House Rules drawn for patrons (Term II) and as second High Table rules (Term VI). */
export const RULE_POOL: readonly HouseRuleId[] = [
  'salted-earth',
  'tarnish',
  'heavy-chains',
  'no-repeats',
  'single-file',
  'toll',
  'iron-ranks',
  'short-hand',
  'rust',
  'heavy-toll',
];

// ---------------------------------------------------------------------------------------------
// Pledges, Terms, Detours
// ---------------------------------------------------------------------------------------------

export const PLEDGES = [
  'pocket-watch',
  'wedding-ring',
  'fiddle',
  'locket',
  'dice',
  'ivory-set',
  'glass-eye',
  'iou',
  'bare-box',
  'scale',
  'shuffled-box',
  'ash',
] as const;
export type PledgeId = (typeof PLEDGES)[number];

export const MAX_TERM = 8;

export const DETOURS = [
  'workbench',
  'furnace',
  'seance',
  'puzzle-box',
  'lost-and-found',
  'twins',
  'fence',
  'auction',
  'confessional',
  'cartographer',
] as const;
export type DetourId = (typeof DETOURS)[number];

/** The Long Night's schedule (docs/SHOP_DESIGN.md §5.1, §21). */
export const LONG_NIGHT = {
  NIGHTS: 3,
  CUT: 12,
  CUT_PATRON_NIGHT1: 8,
  /** Head starts: [Small, Big, High per round] for each Night. */
  ANTES: [
    [0, 25, 30],
    [80, 150, 165],
    [220, 380, 400],
  ] as readonly (readonly [number, number, number])[],
  ANTE_CLOSING: 320,
  ANTE_GROWTH: 1.6,
  /** Each table skipped raises that Night's High Table head start by this fraction. */
  SKIP_RAISE: 0.2,
  BRASS_START: 4,
  BRASS_TABLE: [3, 4, 6] as const,
  THRIFT_ONE_ROUND_MAX: 4,
  THRIFT_HIGH_MAX: 5,
  INTEREST_STEP: 5,
  INTEREST_CAP: 5,
  MANTEL_SLOTS: 5,
  CURIO_SLOTS: 2,
  TRAY_SLOTS: 2,
  LOT_SLOTS: 2,
  RING_COST: 5,
  MELT_COST: 3,
  PAWN_SELL: 3,
  PAWN_HIRE: 6,
  PAWN_BUYBACK: 8,
  FINISH_CHANCE: 12,
  OMENS_PER_NIGHT: [2, 3, 4] as const,
} as const;

export const ALL_STAMPS = STAMPS;
